import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { PrinterIcon, ArrowLeftIcon, MountainsIcon, FilesIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function InvoicePrint() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [platform, setPlatform] = useState({});

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data));
    api.get("/settings/platform/public").then((r) => setPlatform(r.data || {})).catch(() => {});
  }, [id]);

  if (!inv) return <div className="min-h-screen flex items-center justify-center label-overline">Loading invoice…</div>;

  const L = inv.invoice_layout || {};
  const layoutOn = (key) => L[key] !== false; // default ON when unspecified
  const banner = layoutOn("show_header_banner");
  const bannerBg = L.header_bg_color || "#2F5D50";
  const bannerAccent = L.header_accent_color || "#C8A24B";
  const groupOverline = (L.header_group_overline || platform.group_name || "").trim();
  const displayName = (L.header_property_display || "").trim() || inv.property?.name || "";
  const locationBadge = (L.header_location_badge || "").trim() || [inv.property?.city, inv.property?.state].filter(Boolean).join(", ");

  const nights = (() => {
    try {
      const a = new Date(inv.reservation?.check_in), b = new Date(inv.reservation?.check_out);
      return Math.max(1, Math.round((b - a) / 86400000));
    } catch { return 0; }
  })();

  return (
    <div className="min-h-screen bg-muted/40 print:bg-white">
      {/* Toolbar (hidden in print) */}
      <div className="print:hidden sticky top-0 z-10 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between p-4">
          <Button variant="outline" size="sm" onClick={() => window.close()} data-testid="invoice-print-close">
            <ArrowLeftIcon size={14} className="mr-1" /> Close
          </Button>
          <div className="flex items-center gap-2">
            <p className="label-overline">{inv.number}</p>
            <Button variant="outline" size="sm" onClick={() => {
              const token = localStorage.getItem("pms_token");
              fetch(`${API}/invoices/${inv.id}/pdf?split=1`, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((blob) => {
                  const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                  window.open(url, "_blank", "noopener");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                });
            }} data-testid="invoice-print-split-btn">
              <FilesIcon size={14} className="mr-1" /> Split print (rooms · food · extras)
            </Button>
            <Button onClick={() => window.print()} data-testid="invoice-print-btn">
              <PrinterIcon size={14} className="mr-1" /> Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-4xl mx-auto my-8 bg-white shadow-sm border border-border print:shadow-none print:border-0 print:my-0 print:p-0">
        {/* Branded banner header — mirrors the dashboard hero (configurable in
            Admin Console → Invoice layout → Header banner) */}
        {banner && (
          <div className="px-10 py-8 text-white print:px-8" style={{ backgroundColor: bannerBg }} data-testid="invoice-print-banner">
            {groupOverline && <p className="text-[11px] uppercase tracking-[0.28em] mb-3" style={{ color: bannerAccent }}>◆ {groupOverline}</p>}
            <h1 className="font-display text-3xl md:text-4xl leading-tight" style={{ color: bannerAccent }}>
              {displayName}
            </h1>
            {locationBadge && (
              <p
                className="mt-3 inline-block text-[10px] tracking-[0.22em] uppercase border rounded-full px-3 py-1"
                style={{ borderColor: bannerAccent, color: "white" }}
                data-testid="invoice-print-location-badge"
              >
                {locationBadge}
              </p>
            )}
          </div>
        )}
        <div className="p-10 print:p-8">
        {/* Header */}
        <div className="flex items-start justify-between pb-6 border-b border-border">
          <div className="flex items-start gap-3">
            {inv.property?.cover_image && layoutOn("show_logo") ? (
              <img src={inv.property.cover_image} alt="" className="h-14 w-14 object-cover rounded-md" />
            ) : layoutOn("show_logo") ? (
              <div className="h-14 w-14 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <MountainsIcon size={26} weight="duotone" />
              </div>
            ) : null}
            <div>
              <h1 className="font-display text-2xl tracking-tight">{inv.property?.name}</h1>
              {layoutOn("show_property_address") && <>
                <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{inv.property?.address}</p>
                <p className="text-xs text-muted-foreground">{inv.property?.city}, {inv.property?.state}</p>
              </>}
              {layoutOn("show_property_contact") && inv.property?.phone && <p className="text-xs text-muted-foreground">Tel: {inv.property.phone}</p>}
              {layoutOn("show_property_contact") && inv.property?.email && <p className="text-xs text-muted-foreground">Email: {inv.property.email}</p>}
              {layoutOn("show_property_gstin") && (inv.property_gstin || platform.gst_number) && <p className="text-xs mt-1"><span className="text-muted-foreground">GSTIN:</span> <span className="font-mono-plex font-medium" data-testid="invoice-print-property-gstin">{inv.property_gstin || platform.gst_number}</span></p>}
            </div>
          </div>
          <div className="text-right">
            <p className="label-overline">Tax Invoice</p>
            <p className="font-mono-plex text-lg mt-1">{inv.number}</p>
            {layoutOn("show_booking_ref") && inv.booking_ref && <p className="text-xs text-primary font-mono-plex mt-1" data-testid="invoice-print-booking-ref">Booking: {inv.booking_ref}</p>}
            <p className="text-xs text-muted-foreground mt-2">Date: {inv.created_at?.slice(0, 10)}</p>
            {inv.booking_source && <p className="text-xs text-muted-foreground mt-1" data-testid="invoice-print-booking-source">Source: <span className="text-foreground font-medium">{inv.booking_source}</span></p>}
            {inv.payment_methods && <p className="text-xs text-muted-foreground mt-1" data-testid="invoice-print-payment-methods">Payment: <span className="text-foreground font-medium">{inv.payment_methods}</span></p>}
            <p className={`text-xs mt-2 inline-block px-2 py-0.5 rounded ${inv.status === "paid" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"}`}>
              {inv.status?.toUpperCase()}
            </p>
          </div>
        </div>

        {/* Bill to / Stay details */}
        <div className="grid grid-cols-2 gap-8 py-6 border-b border-border">
          <div data-testid="invoice-print-bill-to">
            <p className="label-overline mb-2">Bill to{inv.bill_to_corporate && layoutOn("show_corporate_block") ? " (Corporate)" : ""}</p>
            {inv.bill_to_corporate && layoutOn("show_corporate_block") ? (
              <>
                <p className="font-medium">{inv.corporate_name}</p>
                {inv.corporate_gstin && <p className="text-sm"><span className="text-muted-foreground">GSTIN:</span> <span className="font-mono-plex font-medium" data-testid="invoice-print-corp-gstin">{inv.corporate_gstin}</span></p>}
                {inv.corporate_pan && <p className="text-sm"><span className="text-muted-foreground">PAN:</span> <span className="font-mono-plex font-medium" data-testid="invoice-print-corp-pan">{inv.corporate_pan}</span></p>}
                {inv.corporate_address && <p className="text-sm text-muted-foreground whitespace-pre-line">{inv.corporate_address}</p>}
                {inv.guest?.name && (
                  <div className="mt-2 pt-2 border-t border-border/40">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Attn.</p>
                    <p className="text-sm">{inv.guest.name}</p>
                    {inv.guest.phone && <p className="text-xs text-muted-foreground">{inv.guest.phone}</p>}
                    {inv.guest.email && <p className="text-xs text-muted-foreground">{inv.guest.email}</p>}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="font-medium">{inv.guest?.name}</p>
                <p className="text-sm text-muted-foreground">{inv.guest?.phone}</p>
                {inv.guest?.email && <p className="text-sm text-muted-foreground">{inv.guest.email}</p>}
                {inv.guest?.address && <p className="text-sm text-muted-foreground">{inv.guest.address}</p>}
                {layoutOn("show_guest_id") && inv.guest?.id_number && (
                  <p className="text-xs text-muted-foreground mt-1 uppercase">
                    {inv.guest.id_type}: {inv.guest.id_number}
                  </p>
                )}
                {layoutOn("show_guest_gstin_pan") && inv.guest?.gstin && <p className="text-sm mt-1"><span className="text-muted-foreground">GSTIN:</span> <span className="font-mono-plex font-medium" data-testid="invoice-print-guest-gstin">{inv.guest.gstin}</span></p>}
                {layoutOn("show_guest_gstin_pan") && inv.guest?.pan && <p className="text-sm"><span className="text-muted-foreground">PAN:</span> <span className="font-mono-plex font-medium" data-testid="invoice-print-guest-pan">{inv.guest.pan}</span></p>}
              </>
            )}
          </div>
          {layoutOn("show_stay_details") && (
          <div>
            <p className="label-overline mb-2">Stay details</p>
            <div className="grid grid-cols-2 gap-y-1 text-sm">
              <span className="text-muted-foreground">Room</span>
              <span>{inv.reservation?.room?.number} · {inv.reservation?.room?.room_type}</span>
              <span className="text-muted-foreground">Check-in</span>
              <span>{inv.reservation?.check_in}</span>
              <span className="text-muted-foreground">Check-out</span>
              <span>{inv.reservation?.check_out}</span>
              <span className="text-muted-foreground">Nights</span>
              <span>{nights}</span>
              <span className="text-muted-foreground">Guests</span>
              <span>{inv.reservation?.adults} adult{inv.reservation?.adults > 1 && "s"}{inv.reservation?.children ? `, ${inv.reservation.children} child` : ""}</span>
            </div>
          </div>
          )}
        </div>

        {/* Charges */}
        <table className="w-full mt-6 text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 font-medium text-right w-28">Nights / Qty</th>
              <th className="py-2 font-medium text-right w-28">Rate</th>
              <th className="py-2 font-medium text-right w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/60">
              <td className="py-3">
                Room charges — {inv.reservation?.room?.room_type} · Room {inv.reservation?.room?.number}
                <p className="text-xs text-muted-foreground">{inv.reservation?.check_in} → {inv.reservation?.check_out}</p>
              </td>
              <td className="py-3 text-right" data-testid="invoice-print-room-nights">{nights} Night{nights !== 1 ? "s" : ""}</td>
              <td className="py-3 text-right font-mono-plex">{inr(nights ? inv.room_charges / nights : inv.room_charges)}</td>
              <td className="py-3 text-right font-mono-plex">{inr(inv.room_charges)}</td>
            </tr>
            {(inv.line_items || []).map((li, i) => (
              li.is_header ? (
                <tr key={i} className="border-b border-border/60 bg-brass-50/60">
                  <td className="py-2 font-semibold text-primary" colSpan={4}>{li.description}</td>
                </tr>
              ) : (
                <tr key={i} className="border-b border-border/60">
                  <td className="py-3">{li.description}</td>
                  <td className="py-3 text-right">{li.qty}</td>
                  <td className="py-3 text-right font-mono-plex">{inr(li.rate)}</td>
                  <td className="py-3 text-right font-mono-plex">{inr(li.amount || li.qty * li.rate)}</td>
                </tr>
              )
            ))}
            {inv.extra_charges > 0 && (
              <tr className="border-b border-border/60">
                <td className="py-3">Additional charges</td>
                <td className="py-3 text-right">1</td>
                <td className="py-3 text-right font-mono-plex">{inr(inv.extra_charges)}</td>
                <td className="py-3 text-right font-mono-plex">{inr(inv.extra_charges)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mt-6">
          <div className="w-72 text-sm space-y-1">
            {(() => {
              const subtotal = (inv.room_charges || 0) + (inv.extra_charges || 0) + (inv.line_items || []).reduce((s, li) => s + (li.amount || li.qty * li.rate), 0);
              const discounted = Math.max(0, subtotal - (inv.discount || 0));
              const taxableValue = inv.tax_inclusive ? Math.max(0, discounted - (inv.tax_amount || 0)) : discounted;
              return (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Subtotal <span className="text-[10px] uppercase tracking-wide">({inv.tax_inclusive ? "inclusive" : "exclusive"} of GST)</span>
                    </span>
                    <span className="font-mono-plex">{inr(subtotal)}</span>
                  </div>
                  {inv.discount > 0 && (
                    <div className="flex justify-between text-secondary">
                      <span>Discount</span>
                      <span className="font-mono-plex">– {inr(inv.discount)}</span>
                    </div>
                  )}
                  {inv.tax_inclusive && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Taxable value</span>
                      <span className="font-mono-plex">{inr(taxableValue)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST ({inv.tax_rate}%) {inv.tax_inclusive && <span className="text-[10px] uppercase">— inclusive</span>}</span>
                    <span className="font-mono-plex">{inr(inv.tax_amount)}</span>
                  </div>
                  {layoutOn("show_tax_breakdown") && inv.tax_breakdown && inv.tax_breakdown.length > 1 && (
                    <div className="pl-3 border-l border-border/60 mt-1 space-y-0.5" data-testid="invoice-print-gst-breakdown">
                      {inv.tax_breakdown.map((b, i) => (
                        <div key={i} className="flex justify-between text-[11px] text-muted-foreground">
                          <span>{b.label} <span className="text-[9px] opacity-60">(on {inr(b.taxable)})</span></span>
                          <span className="font-mono-plex">{inr(b.tax)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-border mt-2">
                    <span className="font-medium">Grand Total</span>
                    <span className="font-display text-2xl">{inr(inv.total)}</span>
                  </div>
                  {layoutOn("show_deposit_line") && Number(inv.deposit_applied) > 0 && (
                    <div className="flex justify-between text-[hsl(var(--success))]" data-testid="invoice-print-deposit">
                      <span>Less: Advance / Deposit collected</span>
                      <span className="font-mono-plex">− {inr(inv.deposit_applied)}</span>
                    </div>
                  )}
                  {layoutOn("show_balance_due") && (Number(inv.balance_due) > 0 || Number(inv.deposit_applied) > 0) && (
                    <div className="flex justify-between pt-2 border-t border-border mt-2">
                      <span className="font-medium">Balance due</span>
                      <span className="font-display text-xl" data-testid="invoice-print-balance">{inr(inv.balance_due ?? (inv.total - (inv.paid || 0) - (inv.deposit_applied || 0)))}</span>
                    </div>
                  )}
                </>
              );
            })()}
            {inv.status === "paid" && (
              <div className="flex justify-between text-[hsl(var(--success))]">
                <span>Amount paid</span>
                <span className="font-mono-plex">{inr(inv.paid || inv.total)}</span>
              </div>
            )}
          </div>
        </div>

        {layoutOn("show_payment_reference") && inv.payment_reference && (
          <p className="mt-6 text-xs text-muted-foreground">
            Payment reference: <span className="font-mono-plex">{inv.payment_reference}</span>
            {inv.payment_mode && <span className="ml-2 uppercase tracking-wide">· via {inv.payment_mode}</span>}
          </p>
        )}

        {layoutOn("show_notes") && inv.notes && (
          <div className="mt-8 pt-4 border-t border-border">
            <p className="label-overline mb-1">Notes</p>
            <p className="text-sm whitespace-pre-line">{inv.notes}</p>
          </div>
        )}

        {L.custom_footer_html && (
          <div className="mt-6 text-xs text-muted-foreground whitespace-pre-line" data-testid="invoice-print-custom-footer">{L.custom_footer_html}</div>
        )}

        {/* Footer */}
        {(layoutOn("show_footer_signature") || layoutOn("show_computer_generated_line")) && (
          <div className="mt-10 pt-6 border-t border-border text-center text-xs text-muted-foreground">
            {layoutOn("show_footer_signature") && <p>{inv.invoice_footer_text || `Thank you for choosing ${inv.property?.name}. We look forward to welcoming you back.`}</p>}
            {layoutOn("show_computer_generated_line") && <p className="mt-1">This is a computer-generated invoice.</p>}
          </div>
        )}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
